import * as XLSX from 'xlsx-js-style'
import { saveAs } from 'file-saver'
import { formatDate } from './utils'

// Professional color scheme
const colors = {
  primary: '4472C4',      // Corporate blue
  success: '28A745',      // Green for positive status
  warning: 'FFC107',      // Yellow for caution
  danger: 'DC3545',       // Red for negative status
  light: 'F8F9FA',        // Light gray for alternating rows
  dark: '343A40',         // Dark gray for text
  white: 'FFFFFF',        // White backgrounds
  lightBlue: 'E3F2FD'     // Light blue for accents
}

// Style configurations
const styles = {
  titleStyle: {
    font: { bold: true, sz: 16, color: { rgb: colors.primary } },
    alignment: { horizontal: 'left', vertical: 'center' },
    border: {
      bottom: { style: 'thin', color: { rgb: colors.primary } }
    }
  },
  
  headerStyle: {
    font: { bold: true, sz: 11, color: { rgb: colors.white } },
    fill: { patternType: 'solid', fgColor: { rgb: colors.primary } },
    alignment: { horizontal: 'center', vertical: 'center' },
    border: {
      top: { style: 'thin', color: { rgb: colors.dark } },
      bottom: { style: 'thin', color: { rgb: colors.dark } },
      left: { style: 'thin', color: { rgb: colors.dark } },
      right: { style: 'thin', color: { rgb: colors.dark } }
    }
  },
  
  dataStyle: {
    font: { sz: 10 },
    alignment: { horizontal: 'left', vertical: 'center' },
    border: {
      top: { style: 'thin', color: { rgb: 'CCCCCC' } },
      bottom: { style: 'thin', color: { rgb: 'CCCCCC' } },
      left: { style: 'thin', color: { rgb: 'CCCCCC' } },
      right: { style: 'thin', color: { rgb: 'CCCCCC' } }
    }
  },
  
  dataStyleAlt: {
    font: { sz: 10 },
    fill: { patternType: 'solid', fgColor: { rgb: colors.light } },
    alignment: { horizontal: 'left', vertical: 'center' },
    border: {
      top: { style: 'thin', color: { rgb: 'CCCCCC' } },
      bottom: { style: 'thin', color: { rgb: 'CCCCCC' } },
      left: { style: 'thin', color: { rgb: 'CCCCCC' } },
      right: { style: 'thin', color: { rgb: 'CCCCCC' } }
    }
  },
  
  centerDataStyle: {
    font: { sz: 10 },
    alignment: { horizontal: 'center', vertical: 'center' },
    border: {
      top: { style: 'thin', color: { rgb: 'CCCCCC' } },
      bottom: { style: 'thin', color: { rgb: 'CCCCCC' } },
      left: { style: 'thin', color: { rgb: 'CCCCCC' } },
      right: { style: 'thin', color: { rgb: 'CCCCCC' } }
    }
  },

  infoStyle: {
    font: { sz: 10, color: { rgb: colors.dark } },
    alignment: { horizontal: 'left', vertical: 'center' }
  }
}

// Status-based styles
const getStatusStyle = (status: string, isBackground = false) => {
  const baseStyle = isBackground ? { ...styles.centerDataStyle } : { ...styles.centerDataStyle }
  
  switch (status?.toLowerCase()) {
    case 'approved':
    case 'meets':
      return {
        ...baseStyle,
        fill: { patternType: 'solid', fgColor: { rgb: colors.success } },
        font: { ...baseStyle.font, color: { rgb: colors.white }, bold: true }
      }
    case 'ready_for_review':
    case 'partially_meets':
      return {
        ...baseStyle,
        fill: { patternType: 'solid', fgColor: { rgb: colors.warning } },
        font: { ...baseStyle.font, color: { rgb: colors.dark }, bold: true }
      }
    case 'rejected':
    case 'not_met':
      return {
        ...baseStyle,
        fill: { patternType: 'solid', fgColor: { rgb: colors.danger } },
        font: { ...baseStyle.font, color: { rgb: colors.white }, bold: true }
      }
    case 'pending':
    case 'evaluated':
    default:
      return {
        ...baseStyle,
        fill: { patternType: 'solid', fgColor: { rgb: 'E9ECEF' } },
        font: { ...baseStyle.font, color: { rgb: colors.dark } }
      }
  }
}

// Compliance score style based on percentage
const getComplianceStyle = (score: number) => {
  const baseStyle = { ...styles.centerDataStyle }
  
  if (score >= 80) {
    return {
      ...baseStyle,
      fill: { patternType: 'solid', fgColor: { rgb: colors.success } },
      font: { ...baseStyle.font, color: { rgb: colors.white }, bold: true }
    }
  } else if (score >= 50) {
    return {
      ...baseStyle,
      fill: { patternType: 'solid', fgColor: { rgb: colors.warning } },
      font: { ...baseStyle.font, color: { rgb: colors.dark }, bold: true }
    }
  } else if (score > 0) {
    return {
      ...baseStyle,
      fill: { patternType: 'solid', fgColor: { rgb: colors.danger } },
      font: { ...baseStyle.font, color: { rgb: colors.white }, bold: true }
    }
  } else {
    return {
      ...baseStyle,
      fill: { patternType: 'solid', fgColor: { rgb: 'E9ECEF' } },
      font: { ...baseStyle.font, color: { rgb: colors.dark } }
    }
  }
}

interface VendorResponse {
  id: string
  vendor_name: string
  vendor_email?: string
  submission_date: string
  file_count: number
  file_names: string[]
  status: 'pending' | 'evaluated' | 'ready_for_review' | 'approved' | 'rejected'
  status_display: string
  page_count?: number
  total_file_size?: number
  overall_requirement_status: 'meets' | 'partially_meets' | 'not_met' | 'pending'
}

interface Project {
  id: string
  title: string
  status: string
  due_date?: string
  vendor_responses?: VendorResponse[]
  extraction_status?: 'pending' | 'in_progress' | 'completed' | 'failed'
  requirements?: any[]
}

interface Evaluation {
  id: string
  vendor_response: VendorResponse | string
  requirement_id: string
  result: 'meets' | 'partially_meets' | 'not_met'
  evidence: string
  reasoning: string
  confidence_score: number
  source_page: number
  source_section: string
}

/**
 * Format file size in bytes to human readable format
 */
const formatFileSize = (bytes?: number): string => {
  if (!bytes) return 'N/A'
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + ' ' + sizes[i]
}

/**
 * Get human readable status label
 */
const getStatusLabel = (status: string): string => {
  switch (status) {
    case 'pending': return 'Pending'
    case 'evaluated': return 'Evaluated'
    case 'ready_for_review': return 'Ready for Review'
    case 'approved': return 'Approved'
    case 'rejected': return 'Rejected'
    default: return status.charAt(0).toUpperCase() + status.slice(1)
  }
}

/**
 * Get human readable requirement status label
 */
const getRequirementStatusLabel = (status: string): string => {
  switch (status) {
    case 'meets': return 'Meets All'
    case 'partially_meets': return 'Partially Meets'
    case 'not_met': return 'Does Not Meet'
    case 'pending': return 'Pending Analysis'
    default: return 'Unknown'
  }
}

/**
 * Calculate compliance percentage for a vendor
 */
const calculateComplianceScore = (evaluations: Evaluation[]): number => {
  if (evaluations.length === 0) return 0
  
  let totalScore = 0
  evaluations.forEach(evaluation => {
    switch (evaluation.result) {
      case 'meets': totalScore += 100; break
      case 'partially_meets': totalScore += 50; break
      case 'not_met': totalScore += 0; break
    }
  })
  
  return Math.round(totalScore / evaluations.length)
}

/**
 * Create the vendor summary sheet with professional styling
 */
const createVendorSummarySheet = (project: Project, evaluationsByVendor?: Map<string, Evaluation[]>): XLSX.WorkSheet => {
  const responses = project.vendor_responses || []
  
  // Project header information
  const headerData = [
    ['VENDOR RESPONSE SUMMARY REPORT'],
    [''],
    ['Project Name:', project.title],
    ['Due Date:', project.due_date ? formatDate(project.due_date) : 'No due date'],
    ['Total Requirements:', project.requirements?.length || 0],
    ['Total Vendor Responses:', responses.length],
    ['Export Date:', formatDate(new Date().toISOString())],
    [''],
    ['VENDOR ANALYSIS']
  ]

  // Table headers
  const tableHeaders = [
    'Vendor Name',
    'Submission Date',
    'Files',
    'Requirements Status'
  ]

  // Table data
  const tableData = responses.map(response => {
    return [
      response.vendor_name,
      formatDate(response.submission_date),
      response.file_count,
      getRequirementStatusLabel(response.overall_requirement_status)
    ]
  })

  // Combine all data
  const sheetData = [
    ...headerData,
    tableHeaders,
    ...tableData
  ]

  const worksheet = XLSX.utils.aoa_to_sheet(sheetData)

  // Set column widths
  const colWidths = [
    { wch: 30 }, // Vendor Name
    { wch: 20 }, // Submission Date
    { wch: 10 }, // Files
    { wch: 25 }  // Requirements Status
  ]
  worksheet['!cols'] = colWidths

  // Set row heights
  const rowHeights = [
    { hpt: 25 }, // Title row
    { hpt: 15 }, // Empty row
    { hpt: 18 }, // Info rows
    { hpt: 18 }, // Info rows
    { hpt: 18 }, // Info rows
    { hpt: 18 }, // Info rows
    { hpt: 18 }, // Info rows
    { hpt: 15 }, // Empty row
    { hpt: 20 }, // Table title
    { hpt: 25 }  // Header row
  ]
  
  // Add heights for data rows
  for (let i = 0; i < tableData.length; i++) {
    rowHeights.push({ hpt: 20 })
  }
  
  worksheet['!rows'] = rowHeights

  // Apply professional styling
  const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1')
  
  // Style main title (A1)
  if (worksheet['A1']) {
    worksheet['A1'].s = styles.titleStyle
  }

  // Style project information rows (A3:B7)
  for (let row = 2; row <= 6; row++) {
    const labelCell = XLSX.utils.encode_cell({ r: row, c: 0 })
    const valueCell = XLSX.utils.encode_cell({ r: row, c: 1 })
    
    if (worksheet[labelCell]) {
      worksheet[labelCell].s = {
        ...styles.infoStyle,
        font: { ...styles.infoStyle.font, bold: true }
      }
    }
    if (worksheet[valueCell]) {
      worksheet[valueCell].s = styles.infoStyle
    }
  }

  // Style table title (A9)
  const tableTitleCell = XLSX.utils.encode_cell({ r: 8, c: 0 })
  if (worksheet[tableTitleCell]) {
    worksheet[tableTitleCell].s = {
      ...styles.titleStyle,
      font: { ...styles.titleStyle.font, sz: 12 }
    }
  }

  // Style table headers (row 10)
  const headerRowIndex = 9
  for (let col = 0; col < tableHeaders.length; col++) {
    const cellRef = XLSX.utils.encode_cell({ r: headerRowIndex, c: col })
    if (worksheet[cellRef]) {
      worksheet[cellRef].s = styles.headerStyle
    }
  }

  // Style data rows with alternating colors and status-based styling
  for (let row = 0; row < tableData.length; row++) {
    const dataRowIndex = headerRowIndex + 1 + row
    const response = responses[row]
    
    for (let col = 0; col < tableHeaders.length; col++) {
      const cellRef = XLSX.utils.encode_cell({ r: dataRowIndex, c: col })
      if (worksheet[cellRef]) {
        let cellStyle = row % 2 === 0 ? styles.dataStyle : styles.dataStyleAlt
        
        // Apply special styling for specific columns
        switch (col) {
          case 2: // Files
            cellStyle = { ...cellStyle, alignment: { horizontal: 'center', vertical: 'center' } }
            break
          case 3: // Requirements Status
            cellStyle = getStatusStyle(response.overall_requirement_status)
            break
        }
        
        worksheet[cellRef].s = cellStyle
      }
    }
  }

  return worksheet
}

/**
 * Create requirements matrix sheet showing all requirements vs all vendors
 */
const createRequirementsMatrixSheet = (
  project: Project, 
  requirements: any[]
): XLSX.WorkSheet => {
  const responses = project.vendor_responses || []
  
  const headerData = [
    ['REQUIREMENTS COMPLIANCE MATRIX'],
    [''],
    ['This matrix shows how each vendor performs against every requirement.'],
    ['']
  ]

  // Create headers: Requirement columns + vendor columns
  const headers = [
    'Req ID',
    'Requirement Title', 
    'Category',
    'Priority',
    'Mandatory',
    ...responses.map(r => r.vendor_name)
  ]

  // Create data rows
  const data = requirements.map(requirement => {
    const row = [
      requirement.requirement_id,
      requirement.description || requirement.title || 'No description',
      Array.isArray(requirement.category) ? requirement.category.join(', ') : (requirement.category || 'N/A'),
      requirement.priority || 'medium',
      requirement.is_mandatory ? 'Yes' : 'No'
    ]

    // Add evaluation results for each vendor using overall status
    responses.forEach(response => {
      switch (response.overall_requirement_status) {
        case 'meets': row.push('Yes'); break
        case 'partially_meets': row.push('Partially'); break
        case 'not_met': row.push('No'); break
        default: row.push('Pending')
      }
    })

    return row
  })

  const sheetData = [...headerData, headers, ...data]
  const worksheet = XLSX.utils.aoa_to_sheet(sheetData)

  // Set column widths
  const colWidths = [
    { wch: 12 }, // Req ID
    { wch: 40 }, // Requirement Title
    { wch: 16 }, // Category
    { wch: 10 }, // Priority
    { wch: 10 }, // Mandatory
    ...responses.map(() => ({ wch: 12 })) // Vendor columns
  ]
  worksheet['!cols'] = colWidths

  // Set row heights
  const rowHeights = [
    { hpt: 25 }, // Title
    { hpt: 15 }, // Empty
    { hpt: 18 }, // Description
    { hpt: 15 }, // Empty
    { hpt: 25 }  // Headers
  ]
  
  // Add heights for data rows
  for (let i = 0; i < data.length; i++) {
    rowHeights.push({ hpt: 30 })
  }
  
  worksheet['!rows'] = rowHeights

  // Apply styling
  // Style title (A1)
  if (worksheet['A1']) {
    worksheet['A1'].s = styles.titleStyle
  }

  // Style description (A3)
  if (worksheet['A3']) {
    worksheet['A3'].s = styles.infoStyle
  }

  // Style headers (row 5)
  const headerRowIndex = 4
  for (let col = 0; col < headers.length; col++) {
    const cellRef = XLSX.utils.encode_cell({ r: headerRowIndex, c: col })
    if (worksheet[cellRef]) {
      worksheet[cellRef].s = styles.headerStyle
    }
  }

  // Style data rows
  for (let row = 0; row < data.length; row++) {
    const dataRowIndex = headerRowIndex + 1 + row
    const requirement = requirements[row]
    
    for (let col = 0; col < headers.length; col++) {
      const cellRef = XLSX.utils.encode_cell({ r: dataRowIndex, c: col })
      if (worksheet[cellRef]) {
        let cellStyle = row % 2 === 0 ? styles.dataStyle : styles.dataStyleAlt
        
        // Apply special styling for specific columns
        if (col === 1) { // Requirement Title - wrap text
          cellStyle = {
            ...cellStyle,
            alignment: { horizontal: 'left', vertical: 'top', wrapText: true }
          }
        } else if (col === 3) { // Priority
          const priority = requirement.priority
          if (priority === 'high') {
            cellStyle = {
              ...cellStyle,
              fill: { patternType: 'solid', fgColor: { rgb: 'FFE5E5' } },
              font: { ...cellStyle.font, color: { rgb: colors.danger } }
            }
          } else if (priority === 'medium') {
            cellStyle = {
              ...cellStyle,
              fill: { patternType: 'solid', fgColor: { rgb: 'FFF4E5' } },
              font: { ...cellStyle.font, color: { rgb: colors.warning } }
            }
          }
        } else if (col === 4) { // Mandatory
          if (requirement.is_mandatory) {
            cellStyle = {
              ...cellStyle,
              fill: { patternType: 'solid', fgColor: { rgb: 'FFE5E5' } },
              font: { ...cellStyle.font, color: { rgb: colors.danger }, bold: true }
            }
          }
        } else if (col >= 5) { // Vendor evaluation results
          const cellValue = worksheet[cellRef]?.v
          if (cellValue) {
            cellStyle = getStatusStyle(
              cellValue === 'Yes' ? 'meets' : 
              cellValue === 'Partially' ? 'partially_meets' : 
              cellValue === 'No' ? 'not_met' : 'pending'
            )
          }
        }
        
        worksheet[cellRef].s = cellStyle
      }
    }
  }

  // Freeze panes at header row and requirement columns
  worksheet['!freeze'] = { xSplit: 5, ySplit: 5 }

  return worksheet
}


/**
 * Create dashboard-style project statistics sheet
 */
const createStatisticsSheet = (project: Project, evaluationsByVendor?: Map<string, Evaluation[]>): XLSX.WorkSheet => {
  const responses = project.vendor_responses || []
  
  // Calculate statistics
  const statusCounts = {
    pending: 0,
    evaluated: 0,
    ready_for_review: 0,
    approved: 0,
    rejected: 0
  }

  const requirementStatusCounts = {
    meets: 0,
    partially_meets: 0,
    not_met: 0,
    pending: 0
  }

  let totalComplianceScore = 0
  let vendorsWithScores = 0

  responses.forEach(response => {
    statusCounts[response.status]++
    requirementStatusCounts[response.overall_requirement_status]++
    
    if (evaluationsByVendor) {
      const evaluations = evaluationsByVendor.get(response.id) || []
      if (evaluations.length > 0) {
        totalComplianceScore += calculateComplianceScore(evaluations)
        vendorsWithScores++
      }
    }
  })

  const averageCompliance = vendorsWithScores > 0 ? Math.round(totalComplianceScore / vendorsWithScores) : 0
  const complianceRate = responses.length > 0 ? Math.round((requirementStatusCounts.meets / responses.length) * 100) : 0

  const statisticsData = [
    ['PROJECT PERFORMANCE DASHBOARD'],
    [''],
    ['📊 KEY METRICS'],
    ['Total Vendors:', responses.length],
    ['Average Compliance Score:', `${averageCompliance}%`],
    ['Vendors Fully Compliant:', requirementStatusCounts.meets],
    ['Overall Compliance Rate:', `${complianceRate}%`],
    [''],
    ['📈 RESPONSE STATUS BREAKDOWN'],
    ['Pending Analysis:', statusCounts.pending],
    ['Evaluated:', statusCounts.evaluated],
    ['Ready for Review:', statusCounts.ready_for_review],
    ['Approved:', statusCounts.approved],
    ['Rejected:', statusCounts.rejected],
    [''],
    ['✅ REQUIREMENTS COMPLIANCE'],
    ['Meets All Requirements:', requirementStatusCounts.meets],
    ['Partially Meets Requirements:', requirementStatusCounts.partially_meets],
    ['Does Not Meet Requirements:', requirementStatusCounts.not_met],
    ['Pending Analysis:', requirementStatusCounts.pending],
    [''],
    ['📋 PROJECT SUMMARY'],
    ['Total Requirements:', project.requirements?.length || 0],
    ['RFP Due Date:', project.due_date ? formatDate(project.due_date) : 'Not specified'],
    ['Report Generated:', formatDate(new Date().toISOString())]
  ]

  const worksheet = XLSX.utils.aoa_to_sheet(statisticsData)

  // Set column widths
  worksheet['!cols'] = [
    { wch: 35 }, // Label
    { wch: 18 }  // Value
  ]

  // Set row heights
  const rowHeights = [
    { hpt: 25 }, // Title
    { hpt: 15 }, // Empty
    { hpt: 22 }, // Section headers
  ]
  
  // Add standard height for other rows
  for (let i = 3; i < statisticsData.length; i++) {
    rowHeights.push({ hpt: 20 })
  }
  
  worksheet['!rows'] = rowHeights

  // Apply professional styling
  // Style title (A1)
  if (worksheet['A1']) {
    worksheet['A1'].s = styles.titleStyle
  }

  // Style section headers with icons
  const sectionRows = [2, 8, 15, 21] // Rows with section headers
  sectionRows.forEach(rowIndex => {
    const cellRef = XLSX.utils.encode_cell({ r: rowIndex, c: 0 })
    if (worksheet[cellRef]) {
      worksheet[cellRef].s = {
        ...styles.titleStyle,
        font: { ...styles.titleStyle.font, sz: 12, bold: true },
        fill: { patternType: 'solid', fgColor: { rgb: colors.lightBlue } }
      }
    }
  })

  // Style key metrics with special highlighting
  const keyMetricsRows = [3, 4, 5, 6] // Key metrics rows
  keyMetricsRows.forEach(rowIndex => {
    const labelCell = XLSX.utils.encode_cell({ r: rowIndex, c: 0 })
    const valueCell = XLSX.utils.encode_cell({ r: rowIndex, c: 1 })
    
    if (worksheet[labelCell]) {
      worksheet[labelCell].s = {
        ...styles.infoStyle,
        font: { ...styles.infoStyle.font, bold: true }
      }
    }
    
    if (worksheet[valueCell]) {
      // Apply color coding based on the metric
      let valueStyle = styles.infoStyle
      
      if (rowIndex === 4) { // Average Compliance Score
        valueStyle = getComplianceStyle(averageCompliance)
      } else if (rowIndex === 6) { // Overall Compliance Rate
        valueStyle = getComplianceStyle(complianceRate)
      } else {
        valueStyle = {
          ...styles.infoStyle,
          font: { ...styles.infoStyle.font, bold: true, sz: 12 }
        }
      }
      
      worksheet[valueCell].s = valueStyle
    }
  })

  // Style status breakdown rows with color coding
  const statusRows = [9, 10, 11, 12, 13] // Status breakdown rows
  statusRows.forEach((rowIndex, index) => {
    const labelCell = XLSX.utils.encode_cell({ r: rowIndex, c: 0 })
    const valueCell = XLSX.utils.encode_cell({ r: rowIndex, c: 1 })
    
    if (worksheet[labelCell]) {
      worksheet[labelCell].s = styles.infoStyle
    }
    
    if (worksheet[valueCell]) {
      // Color code based on status type
      const statuses = ['pending', 'evaluated', 'ready_for_review', 'approved', 'rejected']
      const statusStyle = getStatusStyle(statuses[index])
      worksheet[valueCell].s = statusStyle
    }
  })

  // Style compliance breakdown rows
  const complianceRows = [16, 17, 18, 19] // Compliance breakdown rows
  complianceRows.forEach((rowIndex, index) => {
    const labelCell = XLSX.utils.encode_cell({ r: rowIndex, c: 0 })
    const valueCell = XLSX.utils.encode_cell({ r: rowIndex, c: 1 })
    
    if (worksheet[labelCell]) {
      worksheet[labelCell].s = styles.infoStyle
    }
    
    if (worksheet[valueCell]) {
      const complianceStatuses = ['meets', 'partially_meets', 'not_met', 'pending']
      const complianceStyle = getStatusStyle(complianceStatuses[index])
      worksheet[valueCell].s = complianceStyle
    }
  })

  // Style summary rows
  const summaryRows = [22, 23, 24] // Summary rows
  summaryRows.forEach(rowIndex => {
    const labelCell = XLSX.utils.encode_cell({ r: rowIndex, c: 0 })
    const valueCell = XLSX.utils.encode_cell({ r: rowIndex, c: 1 })
    
    if (worksheet[labelCell]) {
      worksheet[labelCell].s = styles.infoStyle
    }
    if (worksheet[valueCell]) {
      worksheet[valueCell].s = styles.infoStyle
    }
  })

  return worksheet
}

/**
 * Generate and download Excel file with vendor responses summary
 */
export const generateVendorResponsesExcel = async (
  project: Project,
  evaluations?: Evaluation[],
  requirements?: any[]
): Promise<void> => {
  try {
    // Group evaluations by vendor response ID
    const evaluationsByVendor = new Map<string, Evaluation[]>()
    if (evaluations) {
      evaluations.forEach(evaluation => {
        // Handle both string ID and object vendor_response
        const vendorId = typeof evaluation.vendor_response === 'string' 
          ? evaluation.vendor_response 
          : evaluation.vendor_response.id
        
        if (!evaluationsByVendor.has(vendorId)) {
          evaluationsByVendor.set(vendorId, [])
        }
        evaluationsByVendor.get(vendorId)!.push(evaluation)
      })
    }

    // Create workbook
    const workbook = XLSX.utils.book_new()

    // Add vendor summary sheet
    const summarySheet = createVendorSummarySheet(project, evaluationsByVendor)
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Vendor Summary')

    // Add requirements matrix sheet if requirements are available
    if (requirements && requirements.length > 0 && project.vendor_responses && project.vendor_responses.length > 0) {
      const matrixSheet = createRequirementsMatrixSheet(project, requirements)
      XLSX.utils.book_append_sheet(workbook, matrixSheet, 'Requirements Matrix')
    }


    // Add statistics sheet
    const statisticsSheet = createStatisticsSheet(project, evaluationsByVendor)
    XLSX.utils.book_append_sheet(workbook, statisticsSheet, 'Project Statistics')

    // Generate Excel file
    const excelBuffer = XLSX.write(workbook, { 
      bookType: 'xlsx', 
      type: 'array',
      cellStyles: true 
    })

    // Create blob and download
    const blob = new Blob([excelBuffer], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    })
    
    // Generate filename with current date
    const today = new Date().toISOString().split('T')[0]
    const sanitizedTitle = project.title.replace(/[^a-zA-Z0-9]/g, '_')
    const filename = `${sanitizedTitle}_VendorResponses_${today}.xlsx`

    saveAs(blob, filename)
  } catch (error) {
    console.error('Error generating Excel file:', error)
    throw new Error('Failed to generate Excel file. Please try again.')
  }
}